import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { PageEmptyState, PageHeader } from "../../../components/Page";
import { PageSectionCard } from "../../../components/layout";
import { AppAutocomplete, AppButton, AppTextField } from "../../../components/form";
import AppIconButton from "../../../components/form/AppIconButton";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import {
  adminDataAdaptorService,
  isWarehouseAdaptorType,
  type DataAdaptor,
} from "../../../services/adminDataAdaptorService";
import {
  formatBytes,
  warehouseAdminService,
  type UsageGroupRow,
  type UsageSummary,
  type WarehouseAuditStatus,
  type WarehouseQueryAudit,
  type WarehouseQueryOrigin,
  type WarehouseVerifiedQuery,
} from "../../../services/warehouseAdminService";

type TabKey = "cost" | "history" | "verified";

const ORIGINS: WarehouseQueryOrigin[] = ["CHAT", "WIDGET", "INSIGHT", "ANALYTICS", "PREP", "ADMIN", "EXPORT", "OTHER"];
const STATUSES: WarehouseAuditStatus[] = ["SUCCEEDED", "FAILED", "CANCELLED", "REJECTED"];
const STATUS_COLOR: Record<WarehouseAuditStatus, "success" | "error" | "warning" | "default"> = {
  SUCCEEDED: "success",
  FAILED: "error",
  CANCELLED: "warning",
  REJECTED: "default",
};

type PickerOption = { value: string; label: string };

/** Single-choice autocomplete over value/label options; {@code value} "" shows the "all" option when given. */
function OptionPicker({
  label,
  options,
  value,
  onChange,
  allLabel,
  minWidth,
}: {
  label: string;
  options: PickerOption[];
  value: string;
  onChange: (value: string) => void;
  allLabel?: string;
  minWidth?: number;
}) {
  const all: PickerOption[] = allLabel ? [{ value: "", label: allLabel }, ...options] : options;
  const selected = all.find((o) => o.value === value) ?? all[0] ?? null;
  return (
    <AppAutocomplete<PickerOption, false, true>
      label={label}
      options={all}
      value={selected ?? undefined}
      disableClearable
      autoHighlight
      openOnFocus
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      onChange={(_, option) => option && onChange(option.value)}
      sx={{ minWidth: minWidth ?? 200 }}
    />
  );
}

function shortSql(sql?: string): string {
  if (!sql) return "";
  const flat = sql.replace(/\s+/g, " ").trim();
  return flat.length > 120 ? `${flat.slice(0, 117)}…` : flat;
}

function GroupTable({ rows, keyLabel }: { rows: UsageGroupRow[]; keyLabel: string }) {
  const { t } = useTranslation();
  if (!rows.length) return <Typography variant="body2" color="text.secondary">{t("warehouseUsage.noData")}</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{keyLabel}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.queries")}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.scanned")}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.cacheHits")}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.failed")}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.rejected")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key ?? "none"}>
              <TableCell>{row.label || row.key || "—"}</TableCell>
              <TableCell align="right">{row.queries.toLocaleString()}</TableCell>
              <TableCell align="right">{formatBytes(row.scannedBytes)}</TableCell>
              <TableCell align="right">{row.cacheHits.toLocaleString()}</TableCell>
              <TableCell align="right">{row.failed.toLocaleString()}</TableCell>
              <TableCell align="right">{row.rejected.toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CostTab() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSummary(null);
    setError(null);
    warehouseAdminService
      .summary(days)
      .then(setSummary)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [days]);

  const maxDayBytes = useMemo(
    () => Math.max(1, ...(summary?.byDay ?? []).map((d) => d.scannedBytes || 0)),
    [summary]
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 260 }}>
        <OptionPicker
          label={t("warehouseUsage.days")}
          options={[1, 7, 30, 90].map((d) => ({ value: String(d), label: t("warehouseUsage.lastDays", { count: d }) }))}
          value={String(days)}
          onChange={(v) => setDays(Number(v))}
        />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {!summary && !error && <CircularProgress size={24} />}
      {summary && (
        <>
          <PageSectionCard title={t("warehouseUsage.byDay")}>
            {summary.byDay.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t("warehouseUsage.noData")}</Typography>
            ) : (
              <Stack spacing={0.5}>
                {summary.byDay.map((d) => (
                  <Stack key={d.day} direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" sx={{ width: 84, flexShrink: 0 }}>{d.day}</Typography>
                    <Box sx={{ flexGrow: 1, bgcolor: "action.hover", borderRadius: 0.5, height: 12 }}>
                      <Box
                        sx={{
                          width: `${Math.max(1, (d.scannedBytes / maxDayBytes) * 100)}%`,
                          bgcolor: "primary.main",
                          height: 12,
                          borderRadius: 0.5,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ width: 150, flexShrink: 0, textAlign: "right" }}>
                      {formatBytes(d.scannedBytes)} · {d.queries}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </PageSectionCard>
          <PageSectionCard title={t("warehouseUsage.byAdaptor")}>
            <GroupTable rows={summary.byAdaptor} keyLabel={t("warehouseUsage.col.adaptor")} />
          </PageSectionCard>
          <PageSectionCard title={t("warehouseUsage.byUser")}>
            <GroupTable rows={summary.byUser} keyLabel={t("warehouseUsage.col.user")} />
          </PageSectionCard>
          <PageSectionCard title={t("warehouseUsage.byOrigin")}>
            <GroupTable rows={summary.byOrigin} keyLabel={t("warehouseUsage.col.origin")} />
          </PageSectionCard>
          <PageSectionCard title={t("warehouseUsage.topQueries")}>
            <HistoryTable rows={summary.topQueries} />
          </PageSectionCard>
        </>
      )}
    </Stack>
  );
}

function HistoryTable({ rows }: { rows: WarehouseQueryAudit[] }) {
  const { t } = useTranslation();
  if (!rows.length) return <Typography variant="body2" color="text.secondary">{t("warehouseUsage.noData")}</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("warehouseUsage.col.time")}</TableCell>
            <TableCell>{t("warehouseUsage.col.adaptor")}</TableCell>
            <TableCell>{t("warehouseUsage.col.origin")}</TableCell>
            <TableCell>{t("warehouseUsage.col.user")}</TableCell>
            <TableCell>{t("warehouseUsage.col.status")}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.scanned")}</TableCell>
            <TableCell align="right">{t("warehouseUsage.col.duration")}</TableCell>
            <TableCell>SQL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(row.executedAt).toLocaleString()}</TableCell>
              <TableCell>{row.adaptorName || row.adaptorId}</TableCell>
              <TableCell>{row.origin}</TableCell>
              <TableCell>{row.username}</TableCell>
              <TableCell>
                <Tooltip title={row.error || ""}>
                  <Chip size="small" color={STATUS_COLOR[row.status]} label={row.status} />
                </Tooltip>
                {row.cacheHit && (
                  <Chip size="small" variant="outlined" sx={{ ml: 0.5 }} label={t("warehouseUsage.cached")} />
                )}
              </TableCell>
              <TableCell align="right">{formatBytes(row.scannedBytes)}</TableCell>
              <TableCell align="right">{row.durationMs != null ? `${(row.durationMs / 1000).toFixed(1)} s` : "—"}</TableCell>
              <TableCell>
                <Tooltip title={<Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>{row.sql}</Box>}>
                  <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{shortSql(row.sql)}</Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function HistoryTab({ adaptors }: { adaptors: DataAdaptor[] }) {
  const { t } = useTranslation();
  const [adaptorId, setAdaptorId] = useState("");
  const [origin, setOrigin] = useState<WarehouseQueryOrigin | "">("");
  const [status, setStatus] = useState<WarehouseAuditStatus | "">("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<WarehouseQueryAudit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const size = 50;

  useEffect(() => {
    setRows(null);
    setError(null);
    warehouseAdminService
      .history({ adaptorId: adaptorId || undefined, origin: origin || undefined, status: status || undefined, page, size })
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [adaptorId, origin, status, page]);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <OptionPicker
          label={t("warehouseUsage.col.adaptor")}
          allLabel={t("warehouseUsage.all")}
          options={adaptors.map((a) => ({ value: a.id, label: a.name }))}
          value={adaptorId}
          onChange={(v) => { setPage(0); setAdaptorId(v); }}
          minWidth={240}
        />
        <OptionPicker
          label={t("warehouseUsage.col.origin")}
          allLabel={t("warehouseUsage.all")}
          options={ORIGINS.map((o) => ({ value: o, label: o }))}
          value={origin}
          onChange={(v) => { setPage(0); setOrigin(v as WarehouseQueryOrigin | ""); }}
        />
        <OptionPicker
          label={t("warehouseUsage.col.status")}
          allLabel={t("warehouseUsage.all")}
          options={STATUSES.map((st) => ({ value: st, label: st }))}
          value={status}
          onChange={(v) => { setPage(0); setStatus(v as WarehouseAuditStatus | ""); }}
        />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!rows && !error && <CircularProgress size={24} />}
      {rows && <HistoryTable rows={rows} />}
      <Stack direction="row" spacing={1}>
        <AppButton size="small" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          {t("warehouseUsage.prev")}
        </AppButton>
        <AppButton size="small" disabled={!rows || rows.length < size} onClick={() => setPage((p) => p + 1)}>
          {t("warehouseUsage.next")}
        </AppButton>
      </Stack>
    </Stack>
  );
}

function VerifiedTab({ adaptors }: { adaptors: DataAdaptor[] }) {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [adaptorId, setAdaptorId] = useState("");
  const [rows, setRows] = useState<WarehouseVerifiedQuery[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id?: string; adaptorId: string; question: string; sql: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await warehouseAdminService.verifiedQueries(adaptorId || undefined));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [adaptorId]);

  useEffect(() => {
    void load();
  }, [load]);

  const adaptorName = (id: string) => adaptors.find((a) => a.id === id)?.name ?? id;

  const save = async () => {
    if (!editing) return;
    setError(null);
    try {
      if (editing.id) {
        await warehouseAdminService.updateVerified(editing.id, editing.question);
      } else {
        await warehouseAdminService.createVerified(editing.adaptorId, editing.question, editing.sql);
      }
      setEditing(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (row: WarehouseVerifiedQuery) => {
    try {
      await confirmDelete({ itemName: row.question });
      await warehouseAdminService.deleteVerified(row.id);
      await load();
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">{t("warehouseUsage.verifiedHelp")}</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
        <OptionPicker
          label={t("warehouseUsage.col.adaptor")}
          allLabel={t("warehouseUsage.all")}
          options={adaptors.map((a) => ({ value: a.id, label: a.name }))}
          value={adaptorId}
          onChange={setAdaptorId}
          minWidth={240}
        />
        <AppButton
          variant="outlined"
          disabled={!adaptors.length}
          onClick={() => setEditing({ adaptorId: adaptorId || adaptors[0]?.id || "", question: "", sql: "" })}
        >
          {t("warehouseUsage.addVerified")}
        </AppButton>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {editing && (
        <PageSectionCard title={editing.id ? t("warehouseUsage.editVerified") : t("warehouseUsage.addVerified")}>
          <Stack spacing={1.5}>
            {!editing.id && (
              <OptionPicker
                label={t("warehouseUsage.col.adaptor")}
                options={adaptors.map((a) => ({ value: a.id, label: a.name }))}
                value={editing.adaptorId}
                onChange={(v) => setEditing({ ...editing, adaptorId: v })}
              />
            )}
            <AppTextField label={t("warehouseUsage.question")} value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} fullWidth />
            <AppTextField
              label="SQL"
              value={editing.sql}
              disabled={!!editing.id}
              onChange={(e) => setEditing({ ...editing, sql: e.target.value })}
              fullWidth
              multiline
              minRows={4}
              inputProps={{ spellCheck: false, style: { fontFamily: "monospace", fontSize: 12 } }}
            />
            <Stack direction="row" spacing={1}>
              <AppButton variant="contained" onClick={() => void save()} disabled={!editing.question.trim() || (!editing.id && !editing.sql.trim())}>
                {t("common.save")}
              </AppButton>
              <AppButton onClick={() => setEditing(null)}>{t("common.cancel")}</AppButton>
            </Stack>
          </Stack>
        </PageSectionCard>
      )}
      {rows.length === 0 ? (
        <PageEmptyState message={t("warehouseUsage.noVerified")} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("warehouseUsage.question")}</TableCell>
                <TableCell>{t("warehouseUsage.col.adaptor")}</TableCell>
                <TableCell>SQL</TableCell>
                <TableCell>{t("warehouseUsage.col.user")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.question}</TableCell>
                  <TableCell>{adaptorName(row.adaptorId)}</TableCell>
                  <TableCell>
                    <Tooltip title={<Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>{row.sql}</Box>}>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{shortSql(row.sql)}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.createdByUsername}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <AppIconButton size="small" aria-label={t("common.edit")} onClick={() => setEditing({ id: row.id, adaptorId: row.adaptorId, question: row.question, sql: row.sql })}>
                      <EditOutlinedIcon fontSize="small" />
                    </AppIconButton>
                    <AppIconButton size="small" aria-label={t("common.delete")} onClick={() => void remove(row)}>
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </AppIconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

export default function WarehouseUsagePage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>("cost");
  const [adaptors, setAdaptors] = useState<DataAdaptor[]>([]);

  useEffect(() => {
    adminDataAdaptorService
      .list()
      .then((list) => setAdaptors(list.filter((a) => isWarehouseAdaptorType(a.type))))
      .catch(() => setAdaptors([]));
  }, []);

  return (
    <Stack spacing={2}>
      <PageHeader title={t("warehouseUsage.title")} subtitle={t("warehouseUsage.subtitle")} />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
        <Tab value="cost" label={t("warehouseUsage.tab.cost")} />
        <Tab value="history" label={t("warehouseUsage.tab.history")} />
        <Tab value="verified" label={t("warehouseUsage.tab.verified")} />
      </Tabs>
      {tab === "cost" && <CostTab />}
      {tab === "history" && <HistoryTab adaptors={adaptors} />}
      {tab === "verified" && <VerifiedTab adaptors={adaptors} />}
    </Stack>
  );
}
